import {
  ROLES,
  STAFF_ACCESS,
  STAFF_MODULES,
  hasStaffAccess,
  type StaffModuleValue,
  type StaffPermissions,
} from '@giftsay/constants';

import { useAppSelector } from '../../../store/hooks.js';
import { useStoreNotifications } from '../../stores/hooks/use-store-notifications.js';

/** Portal path prefix → the screen it belongs to, for route gating and nav filtering. */
const PATH_MODULES: readonly (readonly [string, StaffModuleValue])[] = [
  ['/orders', STAFF_MODULES.ORDERS],
  ['/customers', STAFF_MODULES.CUSTOMERS],
  ['/products', STAFF_MODULES.PRODUCTS],
  ['/taxonomy', STAFF_MODULES.TAXONOMY],
  ['/homepage-builder', STAFF_MODULES.HOMEPAGE],
  ['/navigation', STAFF_MODULES.NAVIGATION],
  ['/promotions/coupons', STAFF_MODULES.COUPONS],
  ['/promotions/discounts', STAFF_MODULES.DISCOUNTS],
  ['/delivery-zones', STAFF_MODULES.DELIVERY_ZONES],
  ['/reviews', STAFF_MODULES.REVIEWS],
  ['/settings/contact', STAFF_MODULES.SETTINGS],
  ['/settings/email', STAFF_MODULES.SETTINGS],
  ['/settings/branding', STAFF_MODULES.SETTINGS],
  ['/settings/shipping', STAFF_MODULES.SETTINGS],
  ['/settings/pages', STAFF_MODULES.SETTINGS],
];

function matchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

export function staffModuleForPath(pathname: string): StaffModuleValue | null {
  const match = PATH_MODULES.find(([prefix]) => matchesPrefix(pathname, prefix));
  return match ? match[1] : null;
}

/**
 * What the signed-in user may do on each vendor-portal screen. Only a store's staff logins are
 * restricted — the store owner and platform staff can see and change everything their role
 * already allows. The API enforces the same rules; this only keeps the UI honest.
 */
export function useStaffAccess() {
  const user = useAppSelector((state) => state.auth.user);
  const isStaffLogin = Boolean(user?.staffGroupId);
  // null = unrestricted. A staff login missing its permissions fails closed (an empty matrix
  // reads as "none" everywhere), never open.
  const permissions: StaffPermissions | null = isStaffLogin
    ? (user?.staffPermissions ?? ({} as StaffPermissions))
    : null;

  const { orderEmails } = useStoreNotifications();

  const canView = (module: StaffModuleValue) =>
    hasStaffAccess(permissions, module, STAFF_ACCESS.VIEW);

  return {
    isStaffLogin,
    /** The store's owner account — the only one that may manage staff. */
    isStoreOwner: user?.role === ROLES.VENDOR && !isStaffLogin,
    canView,
    canEdit: (module: StaffModuleValue) => hasStaffAccess(permissions, module, STAFF_ACCESS.EDIT),
    canViewPath: (pathname: string) => {
      // Order emails switched off by an admin leave the vendor nothing to configure there.
      if (!orderEmails && matchesPrefix(pathname, '/settings/email')) return false;
      const module = staffModuleForPath(pathname);
      return module ? canView(module) : true;
    },
  };
}

/** Shorthand for pages: may the current user change things on this screen? */
export function useCanEdit(module: StaffModuleValue): boolean {
  return useStaffAccess().canEdit(module);
}
